import { useCallback, useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import ConfettiExplosion from "react-confetti-explosion";
import { lighten, Typography, Dialog, DialogContent, Stack, Box, darken } from "@mui/material";

import { ParticleBg } from "components/ParticleBg";

import { fireworkOptions } from "constants/particle-options/fireworks";

import { UserLevel } from "utils/contexts/user-level";
import { getInvertColor } from "utils/themeUtils";

interface LevelDialogProps {
	companyLogo: string;
	open: boolean;
	matchesMobile: boolean;
	userLevel: UserLevel;
	triggerHideLevel: () => void;
}

function getConvettiSound() {
	const sounds = [
		"https://riddlestory-common-assets.s3.eu-central-1.amazonaws.com/audio/Confetti+1.mp3",
		"https://riddlestory-common-assets.s3.eu-central-1.amazonaws.com/audio/Confetti+2.mp3",
		"https://riddlestory-common-assets.s3.eu-central-1.amazonaws.com/audio/Confetti+3.mp3",
	];
	const randomIndex = Math.floor(Math.random() * sounds.length);
	return new Audio(sounds[randomIndex]);
}

export const LevelDialog = ({ companyLogo, open, matchesMobile, userLevel, triggerHideLevel }: LevelDialogProps) => {
	const intl = useIntl();

	const [currentLevel, setCurrentLevel] = useState(
		userLevel?.isLevelUp ? userLevel?.prevLevel : userLevel?.currentLevel,
	);
	const [translateXValue, setTranslateXValue] = useState(-100);
	const [applyTransition, setApplyTransition] = useState(false);
	const [animationStarted, setAnimationStarted] = useState(false);
	const [playFireworks, setPlayFireworks] = useState(false);
	const [openConfettiLevel, setOpenConfettiLevel] = useState(false);
	const confettiSound = useRef(getConvettiSound());
	const levelUpSound = useRef(
		new Audio("https://riddlestory-common-assets.s3.eu-central-1.amazonaws.com/audio/level+up.mp3"),
	);
	const fireworkSound = useRef(
		new Audio("https://riddlestory-common-assets.s3.eu-central-1.amazonaws.com/audio/fireworks+sound.mp3"),
	);

	const description = userLevel?.isLevelUp
		? intl.formatMessage({ id: "USERDASHBOARD.level.level-up" })
		: `+${userLevel?.expGained} EXP`;

	const levelConfettiHandler = useCallback(() => {
		setOpenConfettiLevel(true);

		setTimeout(() => {
			triggerHideLevel();
			// Randomize the confetti sound
			confettiSound.current = getConvettiSound();
		}, 5000);
	}, [triggerHideLevel]);

	useEffect(() => {
		if (!animationStarted) {
			setTimeout(() => {
				setApplyTransition(true);
				setAnimationStarted(true);
			}, 100);
		} else if (userLevel?.isLevelUp) {
			setTranslateXValue(0); // Full EXP meter

			setTimeout(() => {
				setApplyTransition(false);
				setTranslateXValue(-100); // Reset EXP meter

				levelUpSound.current.volume = 0.5;
				levelUpSound.current.play();

				setTimeout(() => {
					setCurrentLevel(userLevel?.currentLevel);
					setApplyTransition(true);
					setTranslateXValue(userLevel?.progressPercentage - 100);
					setPlayFireworks(true);

					fireworkSound.current.volume = 0.5;
					fireworkSound.current.play();

					setTimeout(() => {
						triggerHideLevel();
					}, 11000);
				}, 100); // Short delay to reset EXP meter
			}, 1000); // Duration of the full meter animation
		} else {
			setTranslateXValue(userLevel?.progressPercentage! - 100); // Gains EXP without leveling up

			if (confettiSound) {
				confettiSound.current.volume = 0.5;
				confettiSound.current.play();
			}
			setTimeout(() => {
				levelConfettiHandler();
			}, 100);
		}
	}, [
		userLevel?.isLevelUp,
		userLevel?.progressPercentage,
		userLevel?.currentLevel,
		animationStarted,
		levelConfettiHandler,
		triggerHideLevel,
	]);

	useEffect(() => {
		const fireworksound = fireworkSound.current;
		const confettisound = confettiSound.current;
		const levelupsound = levelUpSound.current;
		// Pause the all sounds when the component is unmounted
		return () => {
			fireworksound.pause();
			confettisound.pause();
			levelupsound.pause();
		};
	}, []);

	return (
		<Dialog
			open={open}
			onClose={triggerHideLevel}
			PaperProps={{
				sx: {
					borderRadius: "2rem",
					...(matchesMobile
						? {
								width: "100%",
								boxShadow: "none",
								bgcolor: "transparent",
						  }
						: {}),
				},
			}}
			slotProps={{
				backdrop: {
					sx: {
						...(matchesMobile
							? {
									bgcolor: "common.white",
							  }
							: {}),
					},
				},
			}}
			sx={{
				...(matchesMobile
					? {
							transform: "translateY(-20rem)",
							height: "calc(100vh + 20rem)",
					  }
					: {}),
			}}
		>
			{openConfettiLevel && (
				<ConfettiExplosion
					particleCount={250}
					particleSize={10}
					duration={3000}
					force={0.8}
					width={matchesMobile ? 600 : 1600}
				/>
			)}
			{playFireworks && <ParticleBg id="tsparticles__firework" options={fireworkOptions} />}
			<DialogContent
				sx={{
					position: "relative",
					...(matchesMobile
						? {
								display: "flex",
								flexDirection: "column",
								justifyContent: "flex-end",
								height: "20rem",
						  }
						: {}),
					p: "2rem 2.4rem",
				}}
			>
				<Stack flexDirection="row" alignItems="center" justifyContent="space-between" mb="2.4rem">
					<Typography component="span" fontWeight={800} fontSize="2rem">
						{intl.formatMessage({ id: "COMMON.level" })} {currentLevel}
					</Typography>
					<Typography
						component="span"
						fontWeight={800}
						fontSize="2rem"
						color="#F67D23"
						sx={{
							...(matchesMobile
								? {
										position: "absolute",
										top: 0,
										left: "50%",
										transform: "translateX(-50%)",
								  }
								: {}),
						}}
					>
						{description}
					</Typography>
					<img src={companyLogo} alt="Company logo" className="user-dashboard-level-logo" />
				</Stack>
				<Stack
					position="relative"
					flexDirection="row"
					alignItems="center"
					justifyContent="space-between"
					p="0.8rem"
					width={matchesMobile ? "100%" : "47rem"}
					borderRadius={9999}
					bgcolor={(theme) =>
						getInvertColor(theme.palette.primary.main) === "#3C4255"
							? darken(theme.palette.primary.main, 0.8)
							: lighten(theme.palette.primary.main, 0.8)
					}
				>
					<Stack
						justifyContent="center"
						alignItems="center"
						zIndex={10}
						width="4.5rem"
						height="4.5rem"
						borderRadius={9999}
						bgcolor={(theme) => theme.palette.primary.main}
						fontWeight={700}
						fontSize="2rem"
						color={(theme) => getInvertColor(theme.palette.primary.main)}
					>
						{currentLevel!}
					</Stack>
					<Stack
						justifyContent="center"
						alignItems="center"
						zIndex={10}
						width="4.5rem"
						height="4.5rem"
						borderRadius={9999}
						bgcolor={(theme) =>
							getInvertColor(theme.palette.primary.main) === "#3C4255"
								? darken(theme.palette.primary.main, translateXValue === 0 ? 0 : 0.2)
								: lighten(theme.palette.primary.main, translateXValue === 0 ? 0 : 0.2)
						}
						fontWeight={700}
						fontSize="2rem"
						color={(theme) => getInvertColor(theme.palette.primary.main)}
					>
						{currentLevel! + 1}
					</Stack>
					{/* ----- EXP METER ----- */}
					<Stack
						position="absolute"
						top="0.8rem"
						left="0.8rem"
						flexDirection="row"
						width="calc(100% - 1.6rem)"
						height="4.5rem"
						borderRadius={9999}
						overflow="hidden"
					>
						<Box
							height="100%"
							width="100%"
							bgcolor={(theme) =>
								getInvertColor(theme.palette.primary.main) === "#3C4255"
									? darken(theme.palette.primary.main, 0.1)
									: lighten(theme.palette.primary.main, 0.1)
							}
							borderRadius={9999}
							sx={{
								transform: `translateX(${translateXValue}%)`,
								transition: applyTransition ? "transform 1s ease-in-out" : "none",
							}}
						/>
					</Stack>
				</Stack>
			</DialogContent>
		</Dialog>
	);
};
